// Main App component with routing
// Reference: spec.md user stories, plan.md routing

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LoginPage } from './pages/auth/LoginPage';
import { RoleSelectionPage } from './pages/onboarding/RoleSelectionPage';
import { WaitingForAssignmentPage } from './pages/onboarding/WaitingForAssignmentPage';
import { WaitingForPMAssignmentPage } from './pages/onboarding/WaitingForPMAssignmentPage';
import { OrganizationChoicePage } from './pages/onboarding/OrganizationChoicePage';
import { OrganizationSetupPage } from './pages/onboarding/OrganizationSetupPage';
import { Layout } from './components/layout/Layout';
import { CreateIssuePage } from './pages/issues/CreateIssuePage';
import { IssueListPage } from './pages/issues/IssueListPage';
import { IssueDetailPage } from './pages/issues/IssueDetailPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { PropertiesPage } from './pages/properties/PropertiesPage';
import { UsersPage } from './pages/users/UsersPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';

// Protected route wrapper - checks for onboarding
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs onboarding
  if (user && !user.onboardingCompleted) {
    // Leaseholders waiting for unit assignment
    if (user.role === 'leaseholder' && user.pendingAssignment) {
      return <Navigate to="/onboarding/waiting" replace />;
    }
    // PMs/Owners waiting for organization assignment
    if ((user.role === 'manager' || user.role === 'owner') && user.pendingAssignment) {
      return <Navigate to="/onboarding/waiting-pm" replace />;
    }
    // PMs/Owners who selected role but need to choose create vs join org
    if ((user.role === 'manager' || user.role === 'owner') && !user.organizationId) {
      return <Navigate to="/onboarding/organization-choice" replace />;
    }
    // All users need role selection if not completed
    return <Navigate to="/onboarding/role" replace />;
  }

  return <>{children}</>;
}

// Onboarding route wrapper - for onboarding pages
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If onboarding is complete, redirect to dashboard
  if (user?.onboardingCompleted) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Public route - redirects to dashboard if already authenticated
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Onboarding routes */}
      <Route
        path="/onboarding/role"
        element={
          <OnboardingRoute>
            <RoleSelectionPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/waiting"
        element={
          <OnboardingRoute>
            <WaitingForAssignmentPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/waiting-pm"
        element={
          <OnboardingRoute>
            <WaitingForPMAssignmentPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/organization-choice"
        element={
          <OnboardingRoute>
            <OrganizationChoicePage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/organization"
        element={
          <OnboardingRoute>
            <OrganizationSetupPage />
          </OnboardingRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="issues" element={<IssueListPage />} />
        <Route path="issues/new" element={<CreateIssuePage />} />
        <Route path="issues/:id" element={<IssueDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="properties" element={<PropertiesPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
